import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { Localization } from "@viamap/viamap2-common";
import { ConflictProfile, WFSEndpointsType } from "./ConflictSearchDialog";
import { KeyValueLine, KeyValuesHorizontal, ViewButtonCheck, ViewButtonDownload, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { useContext, useEffect, useState } from "react";
import { actionSetLayerHierarchy, MapitStateActionType, MapitStateContext } from "src/states/MapitState";
import { addConflictDisplayLayer, removeDisplayLayer } from "src/utils/mapLayerHelpers";
import { GeoJSONSource } from "maplibre-gl";
import { handleClickProfileSearch } from "./ConflictSearchCalculations";
import { Spinner } from "react-bootstrap";
import { Persistence } from "src/managers/Persistence";
import { LayerHierarchyEndNode, LayerInfo } from "src/common/managers/Types";
import { OldHierarchy } from "src/managers/OldHierarchy";
import { featureCollection } from "@turf/helpers";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { writeFile as writeFileXLSX, utils as XlsxUtils } from "xlsx";
import { Feature } from "src/states/ApplicationState";
import { message } from "./Message";

export type ConflictSearchParams = ConflictProfile<WFSEndpointsType> & { geom, buffer?: number };
export type Conflict = WFSEndpointsType & { conflict: any, intersect: any, progress: number, err: string, matchedCount: number };

export default function ConflictSearchResults({ window }: { window: ManagedWindow }) {
   const searchParams: ConflictSearchParams = window.windowParams?.searchParams || {};
   const [results, setResults] = useState<Conflict[]>([]);
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState, dispatch: mapitDispatch } = useContext(MapitStateContext);
   const [currentConflict, setCurrentConflict] = useState<Conflict | null>(null);
   const [onlyConflicts, setOnlyConflicts] = useState(false);

   const filteredResults = onlyConflicts ? results.filter(r => r.matchedCount || r.err) : results;
   const isPages = !!searchParams.pages;
   const sourceLayers = isPages ? Object.groupBy(filteredResults, r => window.windowParams?.searchParams?.pages.find((p) => p.layers.some(l => l.source === r.source && l.layer === r.layer))?.name || "Unknown") : Object.groupBy(filteredResults, r => r.source);

   const totalConflicts = results.reduce((acc, r) => acc + (Math.max(0, r.matchedCount)), 0)

   useEffect(() => {
      let x = mapitState.map?.getMapPop()
      if (x) {
         addConflictDisplayLayer(x, "conflict-search-results-V2");
         return () => {
            removeDisplayLayer(x, "conflict-search-results-V2");
         }
      }
   }, [mapitState.map, searchParams])

   useEffect(() => {
      if (window.windowParams?.searchParams) {
         handleClickProfileSearch(window.windowParams.searchParams, setResults);
         return () => {
            setResults([]);
         }
      }
   }, [searchParams])

   return (
      <AdvancedDragModal
         PosDefault={{ left: "100%", top: "0px", width: "400px", transform: "translate(-100%, 0)" }}
         title={Localization.getText("Conflict search results")}
         onClose={() => windowDispatch(closeWindow(window.id))}
         subHeaderContent={<>
            <ViewButtonCheck checked={onlyConflicts} onCheck={function (e: any, a: any): void {
               setOnlyConflicts(!a);
            }}>{Localization.getText("Only conflicts")}</ViewButtonCheck>
            <ViewButtonDownload options={
               [
                  { type: "xlsx", text: Localization.getText("Conflict simple"), access: Feature.Debugging, onClick: (abortSignal) => ConflictToExcel(abortSignal, results, searchParams) },
                  { type: "xlsx", text: Localization.getText("Conflict detailed"), access: Feature.Debugging, onClick: () => ConflictToExcel(new AbortController().signal, results, searchParams, true) },
               ]
            } />
            <ViewButtonIcon
               disabled={!totalConflicts}
               onClick={async () => {
                  let previouslyLayerHiearchy = structuredClone(mapitState.layerHierarchy);
                  let lowestID = OldHierarchy.extractLowestId(mapitState.layerHierarchy);
                  let lis = await Promise.all(await prepareForPlot(results).then(layers => {
                     return layers.map((layer) => {
                        return new Promise((resolve) => {
                           mapitDispatch({
                              type: MapitStateActionType.AddDataLayer,
                              payload: { layerInfo: layer },
                              callbacks: {
                                 onLayerAdded: (li) => {
                                    resolve(li);
                                 }
                              }
                           })
                        })
                     }) as Promise<LayerInfo>[];
                  }));
                  previouslyLayerHiearchy.children.push(
                     {
                        id: lowestID - 1, isCollapsed: true, name: Localization.getText("Conflict search"), children: lis.map((li: LayerInfo) => {
                           let endNode: LayerHierarchyEndNode = {
                              id: li.layerId
                           }
                           return endNode;
                        })
                     }
                  );
                  mapitDispatch(actionSetLayerHierarchy(previouslyLayerHiearchy));
               }}
               title={Localization.getText("Add as layer")}
            > <MIBIcon icon="addToMap"
               /></ViewButtonIcon>
            <ViewButtonIcon
               disabled={!totalConflicts}
               title={Localization.getText("Plot")}
               onClick={(() => {
                  if (results.length) {
                     prepareForPlot(results).then(layers => {
                        windowDispatch(openWindow(WindowId.PlotScreen, { externalSettings: { dataLayer: layers.map((a) => ({ ...a, visible: true })) } }))
                     });
                  }
               })}><MIBIcon icon="plot" /></ViewButtonIcon>
         </>}
      >
         <ADModalBody >
            {/* <div className="ConflictSummary" > */}
            <KeyValuesHorizontal>
               <KeyValueLine valKey={Localization.getText("Total searches")} value={results.reduce((acc, r) => acc + r.progress, 0)} />
               <KeyValueLine valKey={Localization.getText("Conflicts found")} value={results.reduce((acc, r) => acc + (Math.max(0, r.matchedCount)), 0)} />
               <KeyValueLine valKey={Localization.getText("Searches failed")} value={results.reduce((acc, r) => acc + (r.err ? 1 : 0), 0)} />
               <KeyValueLine valKey={Localization.getText("Sources")} value={Object.keys(sourceLayers).length} />
            </KeyValuesHorizontal>
            {/* </div> */}
            <div className="ConflictSearchScrollContainer">
               {
                  Object.entries(sourceLayers).sort((a, b) => a[0].localeCompare(b[0])).map(([source, conflicts]) => (
                     conflicts ? <ConflictSourceGroup key={source} source={source} conflicts={conflicts} selectedConflict={currentConflict ?? undefined}
                        onLayerClick={(conflict) => {
                           let map = mapitState.map?.getMapPop();
                           if (map && conflict?.conflict) {
                              let source = map.getSource("conflict-search-results-V2") as GeoJSONSource;
                              if (source) {
                                 source.setData(conflict.conflict);
                                 setCurrentConflict(conflict);
                              }
                           } else if (map) {
                              let source = map.getSource("conflict-search-results-V2") as GeoJSONSource;
                              if (source) {
                                 source.setData(featureCollection([]));
                                 setCurrentConflict(null);
                              }
                           }
                        }}
                     /> : null
                  ))
               }
            </div>
         </ADModalBody>
      </AdvancedDragModal >
   );
}

function ConflictSourceGroup({ source, conflicts, onLayerClick, selectedConflict }: { source: string, conflicts: Conflict[], onLayerClick?: (geom) => void, selectedConflict?: Conflict }) {
   const { state: mapitState } = useContext(MapitStateContext);

   return (
      <div className="ConflictSourceGroup">
         <div className="ConflictSourceGroupHeader" >
            <div className="ConflictSourceGroupTitle"><b>{source}</b> </div>
            <span>{Localization.getText("Conflicts")}</span>
         </div>
         <div className="ConflictSourceGroupContent">
            {conflicts.map((conflict, i) => (
               <div key={i} className="ConflictItem" onClick={() => {
                  let map = mapitState.map?.getMapPop();
                  if (map && conflict.conflict) {
                     onLayerClick?.(selectedConflict === conflict ? null : conflict);
                  }
               }}>
                  <div className={selectedConflict === conflict ? "ConflictItemSelected" : ""}>{conflict.title ?? conflict.layer}</div>
                  {
                     conflict.err ? <div className="ConflictItemError">{Localization.getText("Error")}</div> :
                        <div className="ConflictItemDetails">{!conflict.progress ? <Spinner animation="border" size="sm" /> : conflict.matchedCount}</div>
                  }
               </div>
            ))}
         </div>
      </div>
   )
}

async function prepareForPlot(conflicts: Conflict[]) {
   let results = await Promise.allSettled(conflicts.map(async (c) => {
      if (c.conflict && c.conflict.features) {
         return await new Promise((resolve) => {
            Persistence.createLayersFromGeoJSON(c.title ?? c.layer, c.conflict, "", (li) => {
               resolve(li);
            });
         })
      } throw new Error("No conflict data");
   }));
   return results.filter(r => r.status === "fulfilled").map(r => (r as PromiseFulfilledResult<any>).value) as LayerInfo[];
}


function conflictList(conflicts: Conflict[], profile: ConflictProfile<WFSEndpointsType>) {
   let conflictList: { [key: string]: any }[] = []
   if (profile.pages) {
      conflictList = profile.pages.map(p => {
         let matchedConflicts = conflicts.filter(c => p.layers.some(l => l.source === c.source && l.layer === c.layer));
         return matchedConflicts?.map(c => ({
            [Localization.getText("Page")]: p.name,
            [Localization.getText("Source")]: c.source,
            [Localization.getText("Layer")]: c.title ?? c.layer,
            [Localization.getText("Count")]: c.matchedCount
         })) || [];
      }).flat();
   } else {
      conflictList = conflicts.sort((a, b) => a.source.localeCompare(b.source)).map(c => ({
         [Localization.getText("Source")]: c.source,
         [Localization.getText("Layer")]: c.title ?? c.layer,
         [Localization.getText("Count")]: c.matchedCount
      }));
   }
   return conflictList;
}

function conflictItemPage(conflict: Conflict): { [key: string]: any }[] {
   return conflict.conflict.features.map((f) => {
      let props = f.properties || {};
      return props
   })
}

async function ConflictToExcel(abort: AbortSignal, conflicts: Conflict[], profile: ConflictProfile<WFSEndpointsType>, includeDetails = false) {
   if (conflicts.length === 0) {
      message.info(Localization.getText("No conflicts to export"));
      return;
   }
   await new Promise((resolve) => {
      setTimeout(() => {
         resolve(true);
      }, 500);
   });

   let firstSheet = XlsxUtils.json_to_sheet(conflictList(conflicts, profile));
   let xlsxBook = XlsxUtils.book_new(firstSheet, Localization.getText("Conflicts"));
   if (includeDetails) {
      conflicts.forEach((conflict) => {
         let details = conflictItemPage(conflict);
         if (details.length === 0) {
            return;
         }
         let sheet = XlsxUtils.json_to_sheet(details);
         let name = conflict.title ?? conflict.layer;
         if (name.length > 31) {
            name = name.substring(0, 28) + "...";
         }
         XlsxUtils.book_append_sheet(xlsxBook, sheet, name);
      });
   }

   let variantName = Localization.getText(includeDetails ? "detailed" : "simple");
   writeFileXLSX(xlsxBook, `${profile.name.replaceAll(" ", "_")}_${new Date().toLocaleDateString(Localization.getLocale(), {
      dateStyle: "short",
   })}_${variantName}.xlsx`, { bookType: "xlsx" });
}
